/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useMemo, useRef, useState} from "react"
import styles from "./StatsEnrollment.module.css"
import {handleError} from "helpers"
import {admissionService, termsService} from "services"
import {EnrollmentStats} from "types/admissions"
import {Checkbox, Col, Row, Spin} from "antd"
import moment from "moment"
import cx from "classnames"
import {BaseButton, BaseLoading, Icon} from "components"
import {NewStudentStatusSelect} from "components/NewStudentStatusSelect"
import {STATES_OPTIONS, StudentStatus} from "types/students"
import {FieldOperator, FilterKey} from "types/filter"
import {useAdvisors, useAllCampuses, useAllPrograms, useAllShifts, useModel} from "hooks"
import {Auth} from "types/auth"
import {ExportButton, ExportFileType} from "components/ui"
import {exportXlsx, ExportXLSXPayload, XLSXCell} from "helpers/export-xlsx"
import {useHistory} from "react-router-dom"

type Props = {
    form: any
    setState: any
    departmentId: number
    goToStudentTab: () => void
    isReport?: boolean
}

type RowData = {
    id: number
    name: string
    level: 1 | 2 | 3
    children: RowData[]
    data: {
        termId?: number
        startDate?: string
        total: number
        active?: number
        prospects?: number
        applicants?: number
    }[]
    reportParams: {
        campusId: number
        termId?: number
        programId?: number
        shiftId?: number
    }
}

enum ShowColumns {
    Seats = "seats",
    Prospects = "prospects",
    Applicants = "applicants",
    Enrolled = "enrolled",
    Start = "start"
}

// Utility function to convert stats data to RowData[] format
function convertStatsToRowData(stats: EnrollmentStats): RowData[] {
    return stats.campuses.map((campus) => {
        const campusData = stats.data.filter((x) => x.campusId === campus.campusId)
        const programIds = Array.from(new Set(campusData.map((x) => x.programs.map((y) => y.programId)).flat()))
        const programNames = programIds.map((programId) => {
            const programData = campusData
                .map((x) => x.programs)
                .flat()
                .filter((x) => x.programId === programId)
            return programData[0]?.programName
        })

        return {
            id: campus.campusId,
            name: campus.campusName,
            level: 1,
            children: programIds.map((programId) => {
                const programData = campusData
                    .map((x) => x.programs)
                    .flat()
                    .filter((x) => x.programId === programId)
                const programName = programData[0].programName
                const programCode = programData[0].programCode
                const hasDuplicateName = programNames.filter((name) => name === programName).length > 1
                const displayName = hasDuplicateName && programCode ? `${programName} (${programCode})` : programName
                const shiftIds = new Set(programData.map((x) => x.shifts.map((y) => y.shiftId)).flat())

                return {
                    id: programId,
                    name: displayName,
                    level: 2,
                    children: Array.from(shiftIds).map((shiftId) => {
                        const shiftData = programData
                            .map((x) => x.shifts)
                            .flat()
                            .filter((x) => x.shiftId === shiftId)
                        return {
                            id: shiftId,
                            name: shiftData[0].shiftName,
                            level: 3,
                            children: [],
                            data: shiftData.map((x) => ({
                                termId: x.termId,
                                startDate: x.startDate,
                                total: x.total,
                                active: x.active,
                                prospects: x.prospects,
                                applicants: x.applicants
                            })),
                            reportParams: {
                                campusId: campus.campusId,
                                programId: programId,
                                shiftId: shiftId
                            }
                        }
                    }),
                    data: programData.map((x) => ({
                        termId: x.termId,
                        startDate: x.startDate,
                        total: x.total,
                        active: x.active,
                        prospects: x.prospects,
                        applicants: x.applicants
                    })),
                    reportParams: {
                        campusId: campus.campusId,
                        programId: programId
                    }
                }
            }),
            data: campusData.map((x) => {
                return {
                    termId: x.termId,
                    startDate: x.startDate,
                    total: x.total,
                    active: x.active,
                    prospects: x.prospects,
                    applicants: x.applicants
                }
            }),
            reportParams: {
                campusId: campus.campusId
            }
        }
    })
}

export function StatsEnrollment(props: Props) {
    const {form, setState, departmentId, goToStudentTab, isReport = false} = props
    const [stats, setStats] = useState<EnrollmentStats>({
        campuses: [],
        terms: [],
        data: []
    })
    const [isLoading, setIsLoading] = useState(false)
    const [additionalParams, setAdditionalParams] = useState<{
        dateRange?: [string, string]
        advisorProfileIds?: number[]
    }>({})
    const [studentStatuses, setStudentStatuses] = useState<StudentStatus[]>([])
    const [isNavigating, setIsNavigating] = useState(false)
    const {admissionAdvisors} = useAdvisors().data || {}
    const history = useHistory()
    const [showColumns, setShowColumns] = useState<string[]>(Object.values(ShowColumns))
    const isMountedRef = useRef(true)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (form.applySearch) {
            getStatsInfo()
            setState({applySearch: false})
        }
    }, [form])

    useEffect(() => {
        getStatsInfo()
    }, [])

    function getFilter(form) {
        const {campus, program, advisor, term, dateRange, startDateRange} = form
        let filter: any = {}
        if (term) {
            filter.termsIds = term.map(({id}) => id)
        }
        if (campus) {
            filter.campusIds = campus.map(({id}) => id)
        }
        if (program) {
            filter.programIds = program.map(({id}) => id)
        }
        if (advisor) {
            filter.advisorProfileIds = (advisor || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (dateRange) {
            filter.dateRange = [
                moment(dateRange[0]).startOf("date").toISOString(),
                moment(dateRange[1]).endOf("date").toISOString()
            ]
        }
        if (startDateRange) {
            filter.startDateRange = [
                moment(startDateRange[0]).startOf("date").toISOString(),
                moment(startDateRange[1]).endOf("date").toISOString()
            ]
        }
        if (studentStatuses) {
            filter.excludeStudentStatusIds = studentStatuses.map((item) => item.statusId)
        }
        setAdditionalParams({
            dateRange: filter.dateRange,
            advisorProfileIds: filter.advisorProfileIds
        })

        return filter
    }

    async function getStatsInfo() {
        try {
            if (isMountedRef.current) setIsLoading(true)
            const filter = getFilter(props.form)
            const data = await admissionService.getEnrollmentStats({
                departmentId,
                advisorProfileIds: [],
                ...filter
            })

            if (isMountedRef.current) setStats(data)
        } catch (error) {
            if (isMountedRef.current) handleError(error)
        } finally {
            if (isMountedRef.current) setIsLoading(false)
        }
    }

    const rows = useMemo((): RowData[] => {
        const result = convertStatsToRowData(stats)
        return result
    }, [stats])

    const renderContent = () => {
        if (isLoading) {
            return <Spin className={styles.loading} />
        }
        if (rows.length) {
            return (
                <div className={styles.body}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={cx(styles.tableHeaderText, styles.noBorder)} />
                                {stats.terms.map(({termName, termCode, startDates}, index) => {
                                    if (!startDates.length) {
                                        return null
                                    }
                                    return (
                                        <th
                                            key={index}
                                            className={styles.tableHeaderText}
                                            colSpan={showColumns.length * startDates.length}>
                                            <div className={styles.tableHeaderTextTitle}>
                                                <span className={styles.termName}>{termName || "Unknown"}</span>
                                                <span className={styles.termCode}>
                                                    {termName ? `(${termCode})` : ""}
                                                </span>
                                            </div>
                                        </th>
                                    )
                                })}
                            </tr>
                            <tr>
                                <th className={styles.noBorder} />
                                {stats.terms.map(({startDates}) => {
                                    return startDates.map((startDate, index) => {
                                        return (
                                            <th
                                                key={index}
                                                colSpan={showColumns.length}
                                                className={styles.startDateItem}>
                                                <span className={styles.startDateLabel}>{startDate}</span>
                                            </th>
                                        )
                                    })
                                })}
                            </tr>
                            <tr>
                                <th />
                                {stats.terms.map(({startDates}) => {
                                    return startDates.map((_, index) => {
                                        return (
                                            <>
                                                {showColumns.includes(ShowColumns.Seats) && (
                                                    <th
                                                        className={styles.col}
                                                        style={{width: `${100 / showColumns.length}%`}}>
                                                        SEATS
                                                    </th>
                                                )}
                                                {showColumns.includes(ShowColumns.Prospects) && (
                                                    <th
                                                        className={styles.col}
                                                        style={{width: `${100 / showColumns.length}%`}}>
                                                        PROSPECTS
                                                    </th>
                                                )}
                                                {showColumns.includes(ShowColumns.Applicants) && (
                                                    <th
                                                        className={styles.col}
                                                        style={{width: `${100 / showColumns.length}%`}}>
                                                        APPLICANTS
                                                    </th>
                                                )}
                                                {showColumns.includes(ShowColumns.Enrolled) && (
                                                    <th
                                                        className={styles.col}
                                                        style={{width: `${100 / showColumns.length}%`}}>
                                                        ENROLL
                                                    </th>
                                                )}
                                                {showColumns.includes(ShowColumns.Start) && (
                                                    <th
                                                        className={styles.col}
                                                        style={{width: `${100 / showColumns.length}%`}}>
                                                        START
                                                    </th>
                                                )}
                                            </>
                                        )
                                    })
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                return (
                                    <TableCell
                                        key={index}
                                        row={row}
                                        terms={stats.terms}
                                        additionalParams={additionalParams}
                                        targets={stats.targets}
                                        goToStudentTab={goToStudentTab}
                                        setIsNavigating={setIsNavigating}
                                        admissionAdvisors={admissionAdvisors}
                                        showColumns={showColumns}
                                    />
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )
        }
        return <div className={styles.noData}>No data</div>
    }

    const handleExportFile = (type: ExportFileType) => {
        return exportReportToExcel("Admissions Analytics By Term Start Report", stats.terms, rows, stats.targets)
    }

    return (
        <div className={styles.wrap}>
            {!isReport && <p className={styles.title}>Admissions Enrollment</p>}
            <div className={styles.filterContainer}>
                <div className={styles.filterWrap}>
                    <span className={styles.label}>Exclude student statuses:</span>
                    <NewStudentStatusSelect
                        value={studentStatuses}
                        onChange={setStudentStatuses}
                        isMulti
                        className={styles.statusSelect}
                    />
                    <BaseButton title="Apply" onClick={() => getStatsInfo()} />
                </div>
                {isReport ? (
                    <ExportButton onSelect={handleExportFile} availableFileTypes={["excel"]} />
                ) : (
                    <div
                        className={styles.link}
                        onClick={() => {
                            history.push(`/reports/admissions/analytics-by-term`, {
                                backUrl: "/admissions/staff"
                            })
                        }}>
                        Go to Report
                    </div>
                )}
            </div>
            <div className={styles.showColumnsContainer}>
                <span className={styles.showColumnsLabel}>Show columns:</span>
                {Object.values(ShowColumns).map((column) => {
                    return (
                        <div
                            key={column}
                            className={styles.showColumn}
                            onClick={() => {
                                if (showColumns.includes(column)) {
                                    setShowColumns(showColumns.filter((c) => c !== column))
                                } else {
                                    setShowColumns([...showColumns, column])
                                }
                            }}>
                            <Checkbox checked={showColumns.includes(column)} />
                            <span className={styles.showColumnText}>{column}</span>
                        </div>
                    )
                })}
            </div>
            {renderContent()}
            <BaseLoading isShow={isNavigating} />
        </div>
    )
}

interface TableCellProps {
    row: RowData
    terms: {
        termId?: number
        termName: string
        startDates: string[]
    }[]
    additionalParams: {
        dateRange?: [string, string]
        advisorProfileIds?: number[]
    }
    targets: EnrollmentStats["targets"]
    goToStudentTab: () => void
    setIsNavigating: (isNavigating: boolean) => void
    admissionAdvisors: Auth.AdvisorsListItemDetail[]
    showColumns: string[]
}

function TableCell(props: TableCellProps) {
    const model = useModel()
    const {row, terms, additionalParams, targets, goToStudentTab, setIsNavigating, admissionAdvisors, showColumns} =
        props
    const canExpand = row.children.length > 0
    const [expanded, setExpanded] = useState(false)
    const {campuses} = useAllCampuses()
    const {programs} = useAllPrograms()
    const {shifts} = useAllShifts()

    const gotoReport = async (
        _termId?: number,
        _startDate?: string,
        type: "enrolled" | "active" | "prospects" | "applicants" = "enrolled"
    ) => {
        const params = {termId: _termId, ...row.reportParams, ...additionalParams}
        let filters: any = {}
        const {dateRange, termId, campusId, programId, shiftId, advisorProfileIds} = params
        try {
            setIsNavigating(true)
            const term = await termsService.getTerm(termId)
            if (term) {
                filters.startingTerm = {
                    operator: FieldOperator.Equal,
                    value: [term]
                }
            }
            if (campusId) {
                filters.campus = {
                    operator: FieldOperator.Equal,
                    value: [campuses.find((x) => x.id === campusId)]
                }
            }
            if (advisorProfileIds) {
                filters.admAdvisors = {
                    operator: FieldOperator.Equal,
                    value: admissionAdvisors.filter((x) => advisorProfileIds.includes(x.profileId))
                }
            }
            if (programId) {
                filters.program = {
                    operator: FieldOperator.Equal,
                    value: [programs.find((x) => x.id === programId)]
                }
            }
            if (shiftId) {
                filters.shifts = {
                    operator: FieldOperator.Equal,
                    value: [shifts.find((x) => x.shiftId === shiftId)]
                }
            }
            if (dateRange) {
                filters.createdAtRange = {
                    operator: FieldOperator.Between,
                    value: [moment(dateRange[0]), moment(dateRange[1])]
                }
            }
            if (type === "enrolled") {
                filters.fullyEnrolled = {
                    operator: FieldOperator.Equal,
                    value: true
                }
            }
            if (type === "prospects") {
                filters.state = {
                    operator: FieldOperator.Equal,
                    value: STATES_OPTIONS.filter((x) => x.id === Auth.StudentState.Prospect)
                }
            }
            if (type === "applicants") {
                filters.state = {
                    operator: FieldOperator.Equal,
                    value: STATES_OPTIONS.filter((x) => x.id === Auth.StudentState.Applicant)
                }
            }
            model.updateStorageFilter(FilterKey.AdmissionPage_Students, {
                filters,
                search: ""
            })
            goToStudentTab()
        } catch (error) {
            handleError(error)
        } finally {
            setIsNavigating(false)
        }
    }

    return (
        <>
            <tr
                className={cx(styles.row, {
                    [styles.rowLevel1]: row.level === 1
                })}>
                <td className={styles.tableColumn}>
                    <div
                        className={cx(styles.rowHeader, {[styles.noExpandable]: !canExpand})}
                        onClick={() => canExpand && setExpanded(!expanded)}>
                        {canExpand && row.level === 1 && (
                            <button
                                className={cx(styles.collapseBtn2, {
                                    [styles.collapsedBtn2]: expanded
                                })}>
                                <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                            </button>
                        )}
                        <span
                            className={cx(styles.rowHeaderText, {
                                [styles.rowHeaderLevel2]: row.level === 2,
                                [styles.rowHeaderLevel3]: row.level === 3
                            })}>
                            {row.name}
                        </span>
                        {canExpand && row.level === 2 && (
                            <button className={cx(styles.collapseBtn2, {[styles.collapsedBtn2]: expanded})}>
                                <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                            </button>
                        )}
                    </div>
                </td>
                {terms.map(({termId, startDates}, index) => {
                    return (
                        <>
                            {startDates.map((startDate) => {
                                const foundItem = row.data.find((x) => x.termId === termId && x.startDate === startDate)
                                const canGoEnrolledDetail = foundItem && foundItem.total > 0
                                const canGoActiveDetail = foundItem && foundItem.active > 0
                                const canGoProspectsDetail = foundItem && foundItem.prospects > 0
                                const canGoApplicantsDetail = foundItem && foundItem.applicants > 0
                                let target: number
                                if (row.level === 1) {
                                    target = targets?.campusTargets.find(
                                        (x) =>
                                            x.campusId === row.reportParams.campusId &&
                                            x.termId === termId &&
                                            x.startDate === startDate
                                    )?.target
                                } else if (row.level === 2) {
                                    target = targets?.programTargets.find(
                                        (x) =>
                                            x.campusId === row.reportParams.campusId &&
                                            x.programId === row.reportParams.programId &&
                                            x.termId === termId &&
                                            x.startDate === startDate
                                    )?.target
                                } else if (row.level === 3) {
                                    target = targets?.shiftTargets.find(
                                        (x) =>
                                            x.campusId === row.reportParams.campusId &&
                                            x.programId === row.reportParams.programId &&
                                            x.shiftId === row.reportParams.shiftId &&
                                            x.termId === termId &&
                                            x.startDate === startDate
                                    )?.target
                                }

                                return (
                                    <>
                                        {showColumns.includes(ShowColumns.Seats) && (
                                            <td
                                                className={styles.tableCell}
                                                style={{width: `${100 / showColumns.length}%`}}>
                                                {target > 0 ? target : ""}
                                            </td>
                                        )}
                                        {showColumns.includes(ShowColumns.Prospects) && (
                                            <td
                                                className={cx(styles.tableCell, styles.total, {
                                                    [styles.navigable]: canGoProspectsDetail
                                                })}
                                                onClick={() =>
                                                    canGoProspectsDetail && gotoReport(termId, startDate, "prospects")
                                                }>
                                                {canGoProspectsDetail ? foundItem?.prospects : ""}
                                            </td>
                                        )}
                                        {showColumns.includes(ShowColumns.Applicants) && (
                                            <td
                                                className={cx(styles.tableCell, styles.total, {
                                                    [styles.navigable]: canGoApplicantsDetail
                                                })}
                                                onClick={() =>
                                                    canGoApplicantsDetail && gotoReport(termId, startDate, "applicants")
                                                }>
                                                {canGoApplicantsDetail ? foundItem?.applicants : ""}
                                            </td>
                                        )}
                                        {showColumns.includes(ShowColumns.Enrolled) && (
                                            <td
                                                className={cx(styles.tableCell, styles.total, {
                                                    [styles.navigable]: canGoEnrolledDetail
                                                })}
                                                onClick={() =>
                                                    canGoEnrolledDetail && gotoReport(termId, startDate, "enrolled")
                                                }>
                                                {canGoEnrolledDetail ? foundItem?.total : ""}
                                            </td>
                                        )}
                                        {showColumns.includes(ShowColumns.Start) && (
                                            <td
                                                className={cx(styles.tableCell, styles.total, {
                                                    [styles.navigable]: canGoActiveDetail
                                                })}
                                                onClick={() =>
                                                    canGoActiveDetail && gotoReport(termId, startDate, "active")
                                                }>
                                                {canGoActiveDetail ? foundItem?.active : ""}
                                            </td>
                                        )}
                                    </>
                                )
                            })}
                        </>
                    )
                })}
            </tr>
            {expanded &&
                row.children.map((child, index) => {
                    return (
                        <TableCell
                            key={index}
                            row={child}
                            terms={terms}
                            additionalParams={additionalParams}
                            targets={targets}
                            goToStudentTab={goToStudentTab}
                            setIsNavigating={setIsNavigating}
                            admissionAdvisors={admissionAdvisors}
                            showColumns={showColumns}
                        />
                    )
                })}
        </>
    )
}

function exportReportToExcel(filename: string, terms: any[], rows: RowData[], targets: any) {
    const style = {font: {bold: true}}
    const headers = [""]
    const subHeaders = [""]
    const colsWidth = [40]
    terms.forEach((term) => {
        headers.push(`${term.termName || "Unknown"} ${term.termCode ? `(${term.termCode})` : ""}`)
        headers.push(...["", "", "", ""]) // placeholder for prospects, applicants, enrolled, start
        subHeaders.push(...["SEATS", "PROSPECTS", "APPLICANTS", "ENROLL", "START"])
        colsWidth.push(...[10, 10, 10, 10, 10])
    })
    const sheetHeader: XLSXCell[] = headers.map((x) => {
        return {
            v: x,
            s: style
        }
    })
    const subSheetHeader: XLSXCell[] = subHeaders.map((x) => {
        return {
            v: x,
            s: {font: {italic: true}}
        }
    })
    const heading: XLSXCell[][] = [sheetHeader, subSheetHeader]
    const data: any[] = []
    for (const row1stLevel of rows) {
        const row1Data: any[] = [row1stLevel.name]
        terms.forEach(({termId}) => {
            const foundItem = row1stLevel.data.find((x) => x.termId === termId)
            const target = targets?.campusTargets.find(
                (x) => x.campusId === row1stLevel.reportParams.campusId && x.termId === termId
            )?.target
            row1Data.push(target || "")
            row1Data.push(foundItem && foundItem.prospects > 0 ? foundItem.prospects : "")
            row1Data.push(foundItem && foundItem.applicants > 0 ? foundItem.applicants : "")
            row1Data.push(foundItem && foundItem.total > 0 ? foundItem.total : "")
            row1Data.push(foundItem && foundItem.active > 0 ? foundItem.active : "")
        })
        data.push(row1Data)
        if (row1stLevel.children.length > 0) {
            for (const child2ndLevel of row1stLevel.children) {
                const row2Data: any[] = [`  (PROGRAM) ${child2ndLevel.name}`]
                terms.forEach(({termId}) => {
                    const foundItem = child2ndLevel.data.find((x) => x.termId === termId)
                    const target = targets?.shiftTargets.find(
                        (x) =>
                            x.campusId === child2ndLevel.reportParams.campusId &&
                            x.programId === child2ndLevel.reportParams.programId &&
                            x.termId === termId
                    )?.target
                    row2Data.push(target || "")
                    row2Data.push(foundItem && foundItem.prospects > 0 ? foundItem.prospects : "")
                    row2Data.push(foundItem && foundItem.applicants > 0 ? foundItem.applicants : "")
                    row2Data.push(foundItem && foundItem.total > 0 ? foundItem.total : "")
                    row2Data.push(foundItem && foundItem.active > 0 ? foundItem.active : "")
                })
                data.push(row2Data)
                if (child2ndLevel.children.length > 0) {
                    for (const child3rdLevel of child2ndLevel.children) {
                        const row3Data: any[] = [`      (SHIFT) ${child3rdLevel.name}`]
                        terms.forEach(({termId}) => {
                            const foundItem = child3rdLevel.data.find((x) => x.termId === termId)
                            const target = targets?.shiftTargets.find(
                                (x) =>
                                    x.campusId === child3rdLevel.reportParams.campusId &&
                                    x.programId === child3rdLevel.reportParams.programId &&
                                    x.shiftId === child3rdLevel.reportParams.shiftId &&
                                    x.termId === termId
                            )?.target
                            row3Data.push(target || "")
                            row3Data.push(foundItem && foundItem.prospects > 0 ? foundItem.prospects : "")
                            row3Data.push(foundItem && foundItem.applicants > 0 ? foundItem.applicants : "")
                            row3Data.push(foundItem && foundItem.total > 0 ? foundItem.total : "")
                            row3Data.push(foundItem && foundItem.active > 0 ? foundItem.active : "")
                        })
                        data.push(row3Data)
                    }
                }
            }
        }
    }
    const sheets = [
        {
            heading,
            colsWidth,
            data
        }
    ]

    const payload: ExportXLSXPayload = {
        filename,
        sheets
    }
    exportXlsx(payload)
}
